import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect, useRef } from "react";
import "./css/select2.css";

/**
 * Select2 reutilizable con búsqueda asíncrona
 *
 * @param {string} value - ID del valor seleccionado
 * @param {function} onChange - Callback cuando cambia la selección (recibe id)
 * @param {function} fetchOptions - Función async que recibe search y retorna [{id, label}]
 * @param {string} placeholder - Placeholder del input
 * @param {string} label - Label del select
 * @param {boolean} required - Si es campo requerido
 * @param {string} className - Clases CSS adicionales
 * @param {boolean} disabled - Si está deshabilitado
 * @param {string} errorMessage - Mensaje de error
 */
const Select2 = ({
  value = "",
  onChange,
  fetchOptions,
  placeholder = "Seleccionar...",
  label = "",
  required = false,
  className = "",
  disabled = false,
  errorMessage = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimeout = useRef(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cargar opciones iniciales
  useEffect(() => {
    if (isOpen) {
      loadOptions("");
      // Focus en el input de búsqueda
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Buscar mientras se escribe
  useEffect(() => {
    if (!isOpen) return;

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      loadOptions(search);
    }, 300);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  // Obtener label del valor seleccionado
  useEffect(() => {
    if (value && fetchOptions) {
      loadSelectedLabel();
    } else {
      setSelectedLabel("");
    }
  }, [value]);

  const loadOptions = async (searchTerm) => {
    setLoading(true);
    try {
      const results = await fetchOptions(searchTerm);
      setOptions(results || []);
    } catch (error) {
      console.error("Error loading options:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSelectedLabel = async () => {
    try {
      const results = await fetchOptions("");
      const selected = results.find((opt) => opt.id == value);
      if (selected) {
        setSelectedLabel(selected.label);
      }
    } catch (error) {
      console.error("Error loading selected label:", error);
    }
  };

  const handleSelect = (option) => {
    onChange(option.id);
    setSelectedLabel(option.label);
    setIsOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSelectedLabel("");
    setSearch("");
  };

  return (
    <div className={`select2-wrapper ${className}`} ref={wrapperRef}>
      {label && (
        <label className="form-label fw-medium">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}

      <div
        className={`select2-container ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${
          errorMessage ? "is-invalid" : ""
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="select2-selection">
          <Icon icon="mdi:magnify" width="18" className="select2-icon-search" />
          <div className="select2-value">
            {selectedLabel || (
              <span className="select2-placeholder">{placeholder}</span>
            )}
          </div>
          <div className="select2-icons">
            {selectedLabel && !disabled && (
              <button
                type="button"
                className="select2-clear"
                onClick={handleClear}
                tabIndex={-1}
              >
                <Icon icon="mdi:close-circle" width="18" />
              </button>
            )}
            <Icon
              icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"}
              width="20"
              className="select2-arrow"
            />
          </div>
        </div>

        {isOpen && (
          <div className="select2-dropdown">
            <div className="select2-search">
              <input
                ref={searchInputRef}
                type="text"
                className="select2-search-input"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="select2-results">
              {loading ? (
                <div className="select2-loading">
                  <div className="spinner-border spinner-border-sm text-primary" />
                  <span className="ms-2">Cargando...</span>
                </div>
              ) : options.length === 0 ? (
                <div className="select2-no-results">
                  <Icon icon="mdi:magnify-remove-outline" width="32" />
                  <span>No se encontraron resultados</span>
                </div>
              ) : (
                options.map((option) => (
                  <div
                    key={option.id}
                    className={`select2-option ${option.id == value ? "selected" : ""}`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.label}
                    {option.id == value && (
                      <Icon
                        icon="mdi:check"
                        width="18"
                        className="select2-check"
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="text-danger text-sm mt-1">{errorMessage}</div>
      )}
    </div>
  );
};

export default Select2;
